import api from '@/api';

// TEMPORARY: always log CommodityBid integration responses in browser console (remove after QA)
const DEBUG = true;

function debugLog(...args) {
  if (DEBUG) {
    console.log('[CommodityBid]', ...args);
  }
}

/** @returns {Promise<import('axios').AxiosResponse>} */
export function getCommodityBidMe() {
  const url = '/integrations/commoditybid/me';
  debugLog('GET proxy', url, '(same-origin → customer backend)');
  return api.get(url);
}

/** Admin-only: all customers' CommodityBid activity (per-email upstream sync). */
export function getCommodityBidAdminList() {
  return api.get('/integrations/commoditybid/admin/list');
}

/** @param {unknown} payload — response.data from getCommodityBidAdminList */
export function normalizeCommodityBidAdminList(payload) {
  const d = payload && typeof payload === 'object' ? payload : {};
  const items = asArray(d.items).map((row) => {
    const kind = row.type || row.kind || 'production_request';
    if (row.raw && typeof row.raw === 'object') {
      const item = mapItem(row.raw, kind);
      item.customerEmail = row.customerEmail || item.customerEmail || '—';
      item.customerName = row.customerName || item.customerName || item.customerEmail;
      item.typeLabel = row.typeLabel || kindLabel(kind);
      return item;
    }
    return {
      kind,
      id: row.id != null ? String(row.id) : '',
      typeLabel: row.typeLabel || kindLabel(kind),
      title: row.title || '—',
      status: row.status || '—',
      customerEmail: row.customerEmail || '—',
      customerName: row.customerName || row.customerEmail || '—',
      createdAt: row.createdAt ?? row.created_at ?? null,
      supplierName: row.supplierName ?? null,
      amount: row.amount ?? null,
      currency: row.currency ?? 'USD',
      raw: row,
    };
  });
  return {
    items,
    counts: d.counts || { total: items.length, production_request: 0, bid: 0, offer: 0 },
    note: typeof d.note === 'string' ? d.note : '',
  };
}

export { debugLog as commodityBidDebugLog };

const PENDING_STATUSES = new Set(['open', 'pending', 'active', 'submitted', 'created']);

const COMPLETED_STATUSES = new Set([
  'closed',
  'completed',
  'complete',
  'cancelled',
  'canceled',
  'accepted',
  'rejected',
  'declined',
  'expired',
  'awarded',
  'filled',
  'done',
  'archived',
  'resolved',
]);

function asArray(v) {
  return Array.isArray(v) ? v : [];
}

function unwrapPayload(payload) {
  let d = payload && typeof payload === 'object' ? payload : {};
  if (d.data && typeof d.data === 'object' && !Array.isArray(d.data)) {
    d = d.data;
  }
  return d;
}

function normalizeStatus(raw) {
  const s = raw?.status ?? raw?.state ?? raw?.bid_status ?? raw?.request_status ?? '';
  return String(s).trim().toLowerCase();
}

function getStatus(raw) {
  const s = normalizeStatus(raw);
  return s || '—';
}

/** Buyer's production / auction requests (Create Bid). */
export function getProductionRequestLists(d) {
  return [
    ...asArray(d.productionRequests),
    ...asArray(d.production_requests),
    ...asArray(d.requests),
  ];
}

/** Production requests for notification polling (customer-created bids). */
export function getCommodityBidProductionItems(payload) {
  const d = unwrapPayload(payload);
  return getProductionRequestLists(d)
    .map((raw) => mapItem(raw, 'production_request'))
    .filter((item) => item.id);
}

function isPendingProductionRequest(raw) {
  const s = normalizeStatus(raw);
  if (PENDING_STATUSES.has(s)) return true;
  // Newly created requests may omit status — treat as pending (awaiting supplier bids)
  if (!s) return true;
  return false;
}

function isPendingBidOrOffer(raw) {
  return PENDING_STATUSES.has(normalizeStatus(raw));
}

function isCompletedRecord(raw) {
  const s = normalizeStatus(raw);
  if (!s) return false;
  if (COMPLETED_STATUSES.has(s)) return true;
  if (PENDING_STATUSES.has(s)) return false;
  // Unknown status on closed auctions: not listed as pending
  return s !== '—' && s !== 'unknown' && s !== 'draft';
}

function pickStr(raw, ...keys) {
  for (const key of keys) {
    const v = raw?.[key];
    if (v != null && String(v).trim() !== '') return String(v).trim();
  }
  return null;
}

function pickNum(raw, ...keys) {
  for (const key of keys) {
    const v = raw?.[key];
    if (v == null || v === '') continue;
    const n = Number(v);
    if (!Number.isNaN(n) && n !== 0) return n;
    if (String(v).trim() !== '' && String(v).trim() !== '0' && String(v).trim() !== '0.00') {
      return String(v).trim();
    }
  }
  return null;
}

function mapItem(raw, kind) {
  const id = raw.id ?? raw._id ?? raw.request_id ?? raw.reference ?? '';
  const title =
    raw.title ??
    raw.name ??
    raw.productName ??
    raw.linkedProductName ??
    raw.commodity_name ??
    raw.product_name ??
    raw.reference ??
    raw.request_number ??
    raw.auction_title ??
    (id ? String(id) : kind === 'production_request' ? 'Production request' : 'CommodityBid item');

  const status = getStatus(raw);
  const createdAt =
    raw.created_at ?? raw.createdAt ?? raw.updated_at ?? raw.date ?? raw.submitted_at ?? null;

  const orderQty = pickNum(raw, 'orderQuantity', 'order_quantity', 'qty', 'quantity');
  const unitQty = pickStr(raw, 'quantity', 'unit_quantity');

  return {
    kind,
    id: id != null ? String(id) : '',
    title: String(title),
    status,
    createdAt: createdAt != null ? String(createdAt) : null,
    supplierName: raw.supplier_name ?? raw.supplierName ?? raw.vendor_name ?? null,
    amount: raw.amount ?? raw.price ?? raw.bid_amount ?? raw.total ?? null,
    currency: raw.currency ?? 'USD',
    notes:
      pickStr(raw, 'notes', 'summary') ??
      (kind !== 'production_request' ? pickStr(raw, 'description') : null),
    validityDays: raw.validity_days ?? raw.validityDays ?? null,
    category: pickStr(raw, 'productCategory', 'category', 'commodity', 'commodity_name'),
    productDescription: pickStr(raw, 'productDescription', 'product_description'),
    description: pickStr(raw, 'description'),
    shippingType: pickStr(raw, 'shippingType', 'shipping_type', 'shipping'),
    destinationPort: pickStr(raw, 'destinationPort', 'destination_port', 'destination', 'port'),
    priceType: pickStr(raw, 'priceType', 'price_type', 'incoterm'),
    orderQuantity: orderQty != null ? String(orderQty) : null,
    unitQuantity: unitQty,
    raw,
  };
}

/**
 * Label/value rows for UI detail panels (Pending Bids, History, dashboard).
 * @param {ReturnType<typeof mapItem>} item
 */
export function getCommodityBidDetailRows(item) {
  if (!item) return [];
  const rows = [];
  const seen = new Set();
  const add = (label, value) => {
    const v = value != null ? String(value).trim() : '';
    if (!v || v === '—' || v === '0' || v === '0.00') return;
    const key = `${label}:${v}`;
    if (seen.has(key)) return;
    seen.add(key);
    rows.push({ label, value: v });
  };

  if (item.kind === 'production_request') {
    add('Category', item.category);
    add('Product description', item.productDescription);
    add('Request description', item.description);
    add('Order quantity', item.orderQuantity);
    if (item.unitQuantity && item.unitQuantity !== item.orderQuantity) {
      add('Listed quantity', item.unitQuantity);
    }
    add('Incoterm', item.priceType);
    add('Shipping', item.shippingType);
    add('Destination port', item.destinationPort);
    add('Currency', item.currency);
  } else {
    add('Supplier', item.supplierName);
    if (item.amount != null && item.amount !== '') {
      const cur = item.currency && item.currency !== 'USD' ? ` ${item.currency}` : '';
      const prefix = item.currency === 'USD' ? '$' : '';
      const n = Number(item.amount);
      const amt = Number.isNaN(n) ? String(item.amount) : n.toLocaleString(undefined, { maximumFractionDigits: 2 });
      add('Offered price', `${prefix}${amt}${cur}`.trim());
    }
    add('Notes', item.notes);
    add('Description', item.description);
    if (item.validityDays != null) add('Validity', `${item.validityDays} days`);
    add('Category', item.category);
    add('Incoterm', item.priceType);
    add('Shipping', item.shippingType);
    add('Destination', item.destinationPort);
  }

  const raw = item.raw;
  if (raw && typeof raw === 'object') {
    const extraLabels = {
      deliveryDate: 'Delivery date',
      delivery_date: 'Delivery date',
      packaging: 'Packaging',
      paymentTerms: 'Payment terms',
      payment_terms: 'Payment terms',
      origin: 'Origin',
      originPort: 'Origin port',
      origin_port: 'Origin port',
      specifications: 'Specifications',
      quality: 'Quality',
      grade: 'Grade',
      moq: 'MOQ',
      leadTime: 'Lead time',
      lead_time: 'Lead time',
    };
    for (const [key, label] of Object.entries(extraLabels)) {
      if (raw[key] != null && String(raw[key]).trim()) add(label, raw[key]);
    }
  }

  return rows;
}

/** Short subtitle for compact lists (dashboard). */
export function getCommodityBidSummaryLine(item) {
  const parts = [];
  if (item.category) parts.push(item.category);
  if (item.priceType) parts.push(item.priceType);
  if (item.destinationPort) parts.push(item.destinationPort);
  if (item.orderQuantity) parts.push(`Qty ${item.orderQuantity}`);
  if (item.kind !== 'production_request' && item.supplierName) parts.push(item.supplierName);
  return parts.join(' · ') || null;
}

function sortByDateDesc(items) {
  return [...items].sort((a, b) => {
    const ta = a.createdAt ? Date.parse(a.createdAt) : 0;
    const tb = b.createdAt ? Date.parse(b.createdAt) : 0;
    return (Number.isNaN(tb) ? 0 : tb) - (Number.isNaN(ta) ? 0 : ta);
  });
}

/**
 * Pending Bids: buyer production/auction requests + open manufacturer bids + pending supplier offers.
 * productionRequests are the primary source (Create Bid); bids may still be empty.
 * @param {unknown} payload
 */
export function getPendingCommodityBidItems(payload) {
  const d = unwrapPayload(payload);
  const items = [];
  const seen = new Set();

  const push = (raw, kind) => {
    if (!raw || typeof raw !== 'object') return;
    const item = mapItem(raw, kind);
    const key = `${kind}:${item.id || item.title}`;
    if (seen.has(key)) return;
    seen.add(key);
    items.push(item);
  };

  for (const raw of getProductionRequestLists(d)) {
    if (isPendingProductionRequest(raw)) {
      push(raw, 'production_request');
    }
  }

  for (const raw of asArray(d.bids)) {
    if (isPendingBidOrOffer(raw)) {
      push(raw, 'bid');
    }
  }

  for (const raw of asArray(d.offers)) {
    if (isPendingBidOrOffer(raw)) {
      push(raw, 'offer');
    }
  }

  return sortByDateDesc(items);
}

/**
 * Completed / closed CommodityBid activity (history).
 * @param {unknown} payload
 */
export function getCompletedCommodityBidItems(payload) {
  const d = unwrapPayload(payload);
  const items = [];
  const seen = new Set();

  const push = (raw, kind) => {
    if (!raw || typeof raw !== 'object') return;
    if (!isCompletedRecord(raw)) return;
    const item = mapItem(raw, kind);
    const key = `${kind}:${item.id || item.title}`;
    if (seen.has(key)) return;
    seen.add(key);
    items.push(item);
  };

  for (const raw of getProductionRequestLists(d)) {
    push(raw, 'production_request');
  }
  for (const raw of asArray(d.bids)) {
    push(raw, 'bid');
  }
  for (const raw of asArray(d.offers)) {
    push(raw, 'offer');
  }

  return sortByDateDesc(items);
}

/**
 * Dashboard summary: pending activity (production requests first).
 * @param {unknown} payload
 */
export function normalizeCommodityBidMe(payload) {
  const d = unwrapPayload(payload);
  const email = typeof d.email === 'string' ? d.email : '';
  const items = getPendingCommodityBidItems(payload);

  return {
    email,
    items,
    productionRequests: items.filter((i) => i.kind === 'production_request'),
    pendingBids: items.filter((i) => i.kind === 'bid'),
    pendingOffers: items.filter((i) => i.kind === 'offer'),
  };
}

export function kindLabel(kind) {
  if (kind === 'production_request') return 'Request';
  if (kind === 'bid') return 'Bid';
  if (kind === 'offer') return 'Offer';
  return 'Item';
}

export function kindBadgeClass(kind) {
  if (kind === 'production_request') return 'bg-orange-50 text-orange-800 border-orange-100';
  if (kind === 'bid') return 'bg-amber-50 text-amber-800 border-amber-100';
  if (kind === 'offer') return 'bg-emerald-50 text-emerald-800 border-emerald-100';
  return 'bg-slate-50 text-slate-700 border-slate-100';
}
